/**
 * Live Training eval capture debug — ring buffer + optional console mirror.
 * Also maps key labels into Capture QoS events for durable ingest.
 *
 * Enable UI panel: always shown on live training when eval capture is mounted.
 * Console mirror: ?evalDebug=1 or localStorage SMARTTT_EVAL_DEBUG=1
 */

import {
  appendCaptureQos,
  setCaptureQosContext,
} from "./captureQos";

const MAX_ENTRIES = 100;
const entries = [];
const listeners = new Set();

/** Labels that map 1:1 onto Capture QoS lifecycle events. */
const QOS_LABEL_MAP = Object.freeze({
  camera_ready: "camera_open",
  camera_failed: "camera_open",
  recording_started: "recording_start",
  encode_done: "encode_done",
  upload_ok: "upload_done",
  upload_failed: "upload_done",
});

function parseTriState(raw) {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase();
  if (["1", "true", "yes", "on"].includes(s)) return true;
  if (["0", "false", "no", "off"].includes(s)) return false;
  return null;
}

export function isLiveTrainingEvalDebugConsoleEnabled() {
  if (typeof window === "undefined") return false;
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = parseTriState(q.get("evalDebug"));
    if (fromUrl === true) return true;
    if (fromUrl === false) return false;
    const fromLocal = parseTriState(localStorage.getItem("SMARTTT_EVAL_DEBUG"));
    if (fromLocal === true) return true;
    return parseTriState(process.env.REACT_APP_SMARTTT_EVAL_DEBUG) === true;
  } catch {
    return false;
  }
}

function notify() {
  const snapshot = [...entries];
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
}

function maybeEmitCaptureQos(label, data) {
  const qosEvent = QOS_LABEL_MAP[label];
  if (!qosEvent) return;
  const payload =
    data != null && typeof data === "object" ? { ...data } : { detail: data };
  if (label === "camera_failed") {
    payload.ok = false;
    payload.error_class = "encode_fail";
    payload.message = String(data?.message || data || "").slice(0, 240);
  } else if (label === "upload_ok") {
    payload.ok = true;
    payload.error_class = null;
  } else if (label === "upload_failed") {
    payload.ok = false;
    payload.error_class = "error";
    payload.message = String(data?.message || data || "").slice(0, 240);
  }
  try {
    setCaptureQosContext({ flow: payload.flow || "eval" });
    appendCaptureQos(qosEvent, payload);
  } catch {
    /* never break recording for telemetry */
  }
}

export function appendLiveTrainingEvalDebug(label, data = null) {
  const entry = {
    ts: Date.now(),
    label: String(label || "event"),
    data: data != null ? data : undefined,
  };
  entries.push(entry);
  while (entries.length > MAX_ENTRIES) entries.shift();
  if (isLiveTrainingEvalDebugConsoleEnabled()) {
    console.log(`[LiveTraining Eval] ${entry.label}`, data ?? "");
  }
  maybeEmitCaptureQos(entry.label, data);
  notify();
}

export function getLiveTrainingEvalDebugEntries() {
  return [...entries];
}

export function subscribeLiveTrainingEvalDebug(listener) {
  if (typeof listener !== "function") return () => {};
  listeners.add(listener);
  listener(getLiveTrainingEvalDebugEntries());
  return () => listeners.delete(listener);
}

export function clearLiveTrainingEvalDebug() {
  entries.length = 0;
  notify();
}
